import type { PaletteOptions } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/**
 * Warm monochrome: ink on bone, white surfaces, one hairline border. The only accent is the
 * AI Planet green from the logo, pulled back in saturation so it reads as a signal (live, working,
 * on) rather than decoration. Status hues are muted to sit beside it and are shown as pale tints
 * with dark text, never as solid blocks.
 */
const ink = '#1C1B19';

export const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: { main: ink, light: '#4A4843', dark: '#0E0D0C', contrastText: '#FBFAF7' },
  secondary: { main: '#2E7D4F', light: '#9CCBAE', dark: '#1F5C39', contrastText: '#FFFFFF' },
  success: { main: '#3B7A48', light: '#B9D7BF', dark: '#2B5A35', contrastText: '#FFFFFF' },
  warning: { main: '#A86B12', light: '#EDCF94', dark: '#7A4E0E', contrastText: '#FFFFFF' },
  error: { main: '#B03A2E', light: '#E8B0A8', dark: '#862A21', contrastText: '#FFFFFF' },
  info: { main: '#35698F', light: '#AFC8DB', dark: '#244B68', contrastText: '#FFFFFF' },
  background: { default: '#F6F5F1', paper: '#FFFFFF' },
  text: { primary: ink, secondary: '#6F6C66', disabled: '#A3A09A' },
  divider: '#E7E4DE',
  action: { hover: alpha(ink, 0.04), selected: alpha(ink, 0.07), focus: alpha(ink, 0.1) },
};

/** Shadows carry the ink hue so they read as the same light source everywhere. */
export const elevation = {
  hover: `0 2px 8px ${alpha(ink, 0.05)}`,
  float: `0 12px 32px ${alpha(ink, 0.1)}, 0 2px 6px ${alpha(ink, 0.04)}`,
  modal: `0 24px 64px ${alpha(ink, 0.16)}`,
  inset: `0 0 0 1px ${alpha(ink, 0.08)}, 0 1px 2px ${alpha(ink, 0.06)}`,
} as const;

/** Radii step down from containers to controls to marks, so nesting reads without borders doubling up. */
export const radius = { container: 12, control: 8, mark: 6 } as const;
